import { DRXError, isDRXError } from '@/lib/drx-error';
import { FeatureAccess, hasFeatureAccess } from '@/lib/subscription-feature-guards';
import { resolveEffectiveSubscriptionForUser } from '@/lib/subscription-consistency';
import type { JobRepository } from '../repository/job.repository';
import type { IUserRepository } from '../repository/interfaces';
import type { IJobUseCase } from './interfaces/job.usecase.interface';
import type {
  CreateJobData,
  GetJobData,
  UpdateJobData,
  JobSearchFilters,
  CreateJobApplicationData,
  GetJobApplicationData,
  JobApplicationStatus,
  JobStatistics,
} from '../repository/interfaces/job.repository.interface';

function hasAdminOverride(userData: any): boolean {
  const roles = userData?.roles ?? userData?.role;
  const roleList = Array.isArray(roles) ? roles : [roles];
  const normalizedRoles = roleList
    .filter((role: unknown): role is string => typeof role === 'string' && (role as string).trim().length > 0)
    .map((role: string) => role.toUpperCase());
  return normalizedRoles.includes('ADMIN');
}

export class JobUseCase implements IJobUseCase {
  constructor(
    private jobRepository: JobRepository,
    private userRepository: IUserRepository,
  ) {}

  /**
   * Generate search keywords from job data for discoverability.
   */
  private generateSearchKeywords(jobData: Partial<CreateJobData>): string[] {
    const keywords: string[] = [];

    if (jobData.title) keywords.push(...jobData.title.toLowerCase().split(' '));
    if (jobData.danceStyles) keywords.push(...jobData.danceStyles.map((s) => s.toLowerCase()));
    if (jobData.category) keywords.push(jobData.category.toLowerCase());
    if (jobData.location?.city) keywords.push(jobData.location.city.toLowerCase());
    if (jobData.location?.state) keywords.push(jobData.location.state.toLowerCase());

    return [...new Set(keywords)].filter((k) => k.length > 2);
  }

  async createJob(creatorId: string, jobData: CreateJobData): Promise<GetJobData> {
    try {
      // Verify the creator exists and check subscription
      const creatorRecord = await this.userRepository.getUserById(creatorId);
      if (!creatorRecord) {
        throw new DRXError({
          code: 'JOB_CREATION_NOT_AUTHORIZED',
          devMessage: `Creator ${creatorId} not found in users collection`,
          userMessage: 'Unable to verify your permissions for job creation.',
          level: 'error',
        });
      }

      const creatorData = creatorRecord;
      const { tier: creatorTier } = await resolveEffectiveSubscriptionForUser(
        creatorId,
        creatorData,
        { syncUserDoc: true }
      );

      const canCreateJobs =
        hasAdminOverride(creatorData) ||
        hasFeatureAccess(creatorTier, FeatureAccess.CONTACT_BOOK_PROFESSIONALS);

      if (!canCreateJobs) {
        throw new DRXError({
          code: 'JOB_CREATION_NOT_AUTHORIZED',
          devMessage: `Creator ${creatorId} with tier ${creatorTier} lacks CONTACT_BOOK_PROFESSIONALS feature`,
          userMessage: 'Royalty subscription or higher is required to create job postings.',
          level: 'warning',
        });
      }

      // Attach search keywords (business logic)
      const enrichedData: CreateJobData = {
        ...jobData,
        creatorId,
        tags: jobData.tags || [],
      };

      const searchKeywords = this.generateSearchKeywords(enrichedData);

      return await this.jobRepository.createJob({
        ...enrichedData,
        searchKeywords,
      });
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_CREATE_FAILED',
        devMessage: 'Unexpected error during job creation',
        userMessage: 'Could not create your job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJob(jobId: string): Promise<GetJobData | null> {
    try {
      if (!jobId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Job ID is required',
          userMessage: 'Invalid job reference.',
          level: 'warning',
        });
      }
      return await this.jobRepository.getJob(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_FETCH_FAILED',
        devMessage: `Unexpected error fetching job ${jobId}`,
        userMessage: 'Could not load the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobsByCreator(creatorId: string, status?: string): Promise<GetJobData[]> {
    try {
      if (!creatorId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Creator ID is required',
          userMessage: 'Invalid user reference.',
          level: 'warning',
        });
      }
      return await this.jobRepository.getJobsByCreator(creatorId, status);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_FETCH_FAILED',
        devMessage: `Unexpected error fetching jobs for creator ${creatorId}`,
        userMessage: 'Could not load your job postings. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async searchJobs(filters: JobSearchFilters, limitCount?: number): Promise<GetJobData[]> {
    try {
      return await this.jobRepository.searchJobs(filters, limitCount);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_SEARCH_FAILED',
        devMessage: 'Unexpected error searching jobs',
        userMessage: 'Could not search job postings. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateJob(jobId: string, updateData: UpdateJobData): Promise<void> {
    try {
      if (!jobId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Job ID is required for update',
          userMessage: 'Invalid job reference.',
          level: 'warning',
        });
      }
      await this.jobRepository.updateJob(jobId, updateData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_UPDATE_FAILED',
        devMessage: `Unexpected error updating job ${jobId}`,
        userMessage: 'Could not update the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteJob(jobId: string): Promise<void> {
    try {
      if (!jobId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Job ID is required for deletion',
          userMessage: 'Invalid job reference.',
          level: 'warning',
        });
      }
      await this.jobRepository.deleteJob(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_DELETE_FAILED',
        devMessage: `Unexpected error deleting job ${jobId}`,
        userMessage: 'Could not delete the job posting. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async applyToJob(applicationData: CreateJobApplicationData): Promise<string> {
    try {
      if (!applicationData.jobId || !applicationData.applicantId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Job ID and applicant ID are required to apply',
          userMessage: 'Invalid application data.',
          level: 'warning',
        });
      }

      // Verify the job exists and is accepting applications
      const job = await this.jobRepository.getJob(applicationData.jobId);
      if (!job) {
        throw new DRXError({
          code: 'JOB_NOT_FOUND',
          devMessage: `Job ${applicationData.jobId} not found`,
          userMessage: 'This job posting no longer exists.',
          level: 'warning',
        });
      }
      if (job.status !== 'active') {
        throw new DRXError({
          code: 'JOB_NOT_ACCEPTING_APPLICATIONS',
          devMessage: `Job ${applicationData.jobId} has status ${job.status}, not active`,
          userMessage: 'This job is no longer accepting applications.',
          level: 'warning',
        });
      }

      return await this.jobRepository.applyToJob(applicationData);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_FAILED',
        devMessage: 'Unexpected error submitting job application',
        userMessage: 'Could not submit your application. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobApplications(jobId: string): Promise<GetJobApplicationData[]> {
    try {
      if (!jobId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Job ID is required to fetch applications',
          userMessage: 'Invalid job reference.',
          level: 'warning',
        });
      }
      return await this.jobRepository.getJobApplications(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_FETCH_FAILED',
        devMessage: `Unexpected error fetching applications for job ${jobId}`,
        userMessage: 'Could not load job applications. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateApplicationStatus(
    applicationId: string,
    status: JobApplicationStatus,
    employerNotes?: string
  ): Promise<void> {
    try {
      if (!applicationId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Application ID is required to update status',
          userMessage: 'Invalid application reference.',
          level: 'warning',
        });
      }
      await this.jobRepository.updateApplicationStatus(applicationId, status, employerNotes);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_UPDATE_FAILED',
        devMessage: `Unexpected error updating application ${applicationId}`,
        userMessage: 'Could not update the application status. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getUserApplications(userId: string): Promise<GetJobApplicationData[]> {
    try {
      if (!userId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'User ID is required to fetch applications',
          userMessage: 'Invalid user reference.',
          level: 'warning',
        });
      }
      return await this.jobRepository.getUserApplications(userId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_APPLICATION_FETCH_FAILED',
        devMessage: `Unexpected error fetching applications for user ${userId}`,
        userMessage: 'Could not load your applications. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getJobStatistics(creatorId: string): Promise<JobStatistics> {
    try {
      if (!creatorId) {
        throw new DRXError({
          code: 'JOB_INVALID_INPUT',
          devMessage: 'Creator ID is required for statistics',
          userMessage: 'Invalid user reference.',
          level: 'warning',
        });
      }
      return await this.jobRepository.getJobStatistics(creatorId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_STATS_FAILED',
        devMessage: `Unexpected error computing statistics for creator ${creatorId}`,
        userMessage: 'Could not load job statistics. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async incrementViewCount(jobId: string): Promise<void> {
    try {
      if (!jobId) return;
      await this.jobRepository.incrementViewCount(jobId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'JOB_UPDATE_FAILED',
        devMessage: `Unexpected error incrementing view count for job ${jobId}`,
        userMessage: 'Could not update view count.',
        cause: error,
        level: 'warning',
      });
    }
  }
}
